import { useState, useEffect, useCallback } from "react"
import { AlertTriangle, XCircle, CheckCircle2, TrendingDown, TrendingUp, X, ShieldAlert } from "lucide-react"
import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"

type Band = 'high' | 'medium' | 'low' | 'fail'

interface BandInfo {
  label: string
  scoreRange: string
  deploymentStatus: string
  requiredAction: string
  bg: string
  border: string
  text: string
  subText: string
  badgeCls: string
}

const BANDS: Record<Band, BandInfo> = {
  high: {
    label: 'High',
    scoreRange: '> 95%',
    deploymentStatus: 'Full deployment approved',
    requiredAction: 'No action required — standard monitoring applies',
    bg: 'bg-green-50',
    border: 'border-green-400',
    text: 'text-green-900',
    subText: 'text-green-700',
    badgeCls: 'bg-green-100 text-green-800 border-green-300 hover:bg-green-100',
  },
  medium: {
    label: 'Medium',
    scoreRange: '80 – 95%',
    deploymentStatus: 'Limited deployment',
    requiredAction: 'Enhanced monitoring, phased rollout, stakeholder approval required',
    bg: 'bg-amber-50',
    border: 'border-amber-400',
    text: 'text-amber-900',
    subText: 'text-amber-700',
    badgeCls: 'bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-100',
  },
  low: {
    label: 'Low',
    scoreRange: '65 – 79%',
    deploymentStatus: 'Human review required',
    requiredAction: 'Trigger fallback responses; flag for human review before proceeding',
    bg: 'bg-orange-50',
    border: 'border-orange-400',
    text: 'text-orange-900',
    subText: 'text-orange-700',
    badgeCls: 'bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-100',
  },
  fail: {
    label: 'Fail',
    scoreRange: '< 65%',
    deploymentStatus: 'Deployment blocked',
    requiredAction: 'Review agent meta-behaviour and knowledge content; re-evaluate before redeployment',
    bg: 'bg-red-50',
    border: 'border-red-400',
    text: 'text-red-900',
    subText: 'text-red-700',
    badgeCls: 'bg-red-100 text-red-800 border-red-300 hover:bg-red-100',
  },
}

const BAND_ORDER: Band[] = ['high', 'medium', 'low', 'fail']

function scoreToBand(score: number): Band {
  if (score > 95) return 'high'
  if (score >= 80) return 'medium'
  if (score >= 65) return 'low'
  return 'fail'
}

interface StoredBandState {
  band: Band
  score: number
  teamId: string
  timestamp: number
}

export interface ConfidenceBandBannerProps {
  thumbsUpRate: string
  ratingTotal: number
  teamId?: string
}

const STORAGE_KEY = 'signpost-confidence-band'
const MIN_RATED_RESPONSES = 5

export function ConfidenceBandBanner({ thumbsUpRate, ratingTotal, teamId }: ConfidenceBandBannerProps) {
  const [previousBand, setPreviousBand] = useState<Band | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [initialized, setInitialized] = useState(false)

  const score = thumbsUpRate === 'N/A' || !thumbsUpRate ? null : Number(thumbsUpRate)
  const currentBand = score !== null ? scoreToBand(score) : null

  const storageKey = teamId ? `${STORAGE_KEY}-${teamId}` : STORAGE_KEY

  useEffect(() => {
    if (currentBand === null) return
    try {
      const stored = localStorage.getItem(storageKey)
      if (stored) {
        const parsed: StoredBandState = JSON.parse(stored)
        if (parsed.band !== currentBand) {
          setPreviousBand(parsed.band)
          setDismissed(false)
        }
      }
      localStorage.setItem(storageKey, JSON.stringify({
        band: currentBand,
        score,
        teamId: teamId ?? 'default',
        timestamp: Date.now(),
      } satisfies StoredBandState))
    } catch {}
    setInitialized(true)
  }, [currentBand, storageKey])

  const handleDismiss = useCallback(() => setDismissed(true), [])

  // Not enough data to determine band
  if (!initialized || currentBand === null || ratingTotal < MIN_RATED_RESPONSES) return null
  if (dismissed) return null

  const config = BANDS[currentBand]

  const prevIdx = previousBand ? BAND_ORDER.indexOf(previousBand) : -1
  const currIdx = BAND_ORDER.indexOf(currentBand)
  const isDecline = previousBand && currIdx > prevIdx
  const isRecovery = previousBand && currIdx < prevIdx

  const TransitionIcon = isDecline ? TrendingDown : TrendingUp

  const BandIcon =
    currentBand === 'high' ? CheckCircle2
    : currentBand === 'fail' ? XCircle
    : AlertTriangle

  const iconColor =
    currentBand === 'high' ? 'text-green-600'
    : currentBand === 'fail' ? 'text-red-600'
    : currentBand === 'low' ? 'text-orange-500'
    : 'text-amber-500'

  // The banner renders at every band, including High as a steady-state status.
  // Only announce it assertively when it actually needs attention.
  const bannerRole = currentBand === 'high' ? 'status' : 'alert'

  return (
    <div
      className={cn(
        "relative rounded-lg border-2 p-4 transition-all",
        config.bg,
        config.border,
      )}
      role={bannerRole}
    >
      <Button
        variant="ghost"
        size="icon"
        className={cn("absolute top-2 right-2 h-6 w-6 rounded-full", config.text, "hover:bg-black/10")}
        onClick={handleDismiss}
        aria-label="Dismiss confidence band banner"
      >
        <X className="h-3 w-3" />
      </Button>

      <div className="flex items-start gap-3 pr-8">
        <div className="flex items-center gap-1.5 shrink-0 mt-0.5">
          <ShieldAlert className={cn("h-5 w-5", iconColor)} />
        </div>
        <div className="flex-1 min-w-0 space-y-2">
          {/* Header row */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={cn("font-semibold text-sm", config.text)}>
              AI Safety Confidence Band
            </span>
            <Badge className={cn("text-xs font-semibold border", config.badgeCls)}>
              {config.label}
            </Badge>
            <span className={cn("text-sm font-medium tabular-nums", config.text)}>
              {score}%
            </span>
            <span className={cn("text-xs", config.subText)}>
              ({config.scoreRange})
            </span>
            {previousBand && (
              <Badge
                variant="outline"
                className={cn(
                  "text-xs gap-1",
                  config.text,
                  config.border,
                )}
              >
                <TransitionIcon className="h-3 w-3" />
                {BANDS[previousBand].label} → {config.label}
              </Badge>
            )}
          </div>

          {/* Status + required action */}
          <div className={cn("grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm", config.text)}>
            <div>
              <span className="font-medium">Deployment Status: </span>
              <span className={config.subText}>{config.deploymentStatus}</span>
            </div>
            <div>
              <span className="font-medium">Required Action: </span>
              <span className={config.subText}>{config.requiredAction}</span>
            </div>
          </div>

          {/* Source note */}
          <p className={cn("text-xs", config.subText)}>
            Score based on moderator feedback rating ({ratingTotal} rated responses).
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Derives the confidence band from a thumbsUpRate string.
 * Returns null when there is insufficient data.
 */
export function getConfidenceBand(thumbsUpRate: string, ratingTotal: number): Band | null {
  if (thumbsUpRate === 'N/A' || !thumbsUpRate || ratingTotal < MIN_RATED_RESPONSES) return null
  return scoreToBand(Number(thumbsUpRate))
}

export { BANDS, scoreToBand, type Band }
